import { z } from "zod";
import type { Override, Without } from "@/shared/types";
import { type ZodObjectWithTelemetry, zodObjectWithTelemetry } from "@/telemetry/helpers/zod";
import {
  type FilterHandlersByName,
  internalEventsDef,
  type PluginConfigDefinition,
  type PluginContextDefinition,
  type PluginDefinition,
  type PluginEventsDefinition,
  type PluginHandlerDefinition,
  type PluginHandlerStateDefinition,
  type TypedPluginBuilder,
} from "./types";

/**
 * ### `definePluginConfig()`
 *
 * Standalone version of `definePlugin().config()` used to break down plugin definition.
 *
 * Its output must then be passed to `definePlugin().$config()`.
 *
 * @example
 * ```ts
 * const configDef = definePluginConfig({ schema: z.object({ name: z.string() }) });
 * const plugin = definePlugin("my-plugin").$config(configDef); // <-- Here
 *
 * // Equivalent to:
 * const plugin = definePlugin("my-plugin").config({ schema: z.object({ name: z.string() }) }); // <-- Here
 * ```
 * &nbsp;
 *
 * ---
 * @returns The config definition.
 */
export const definePluginConfig = <Schema extends z.ZodObject>(
  definition: ZodObjectWithTelemetry<Schema, "input">,
) => zodObjectWithTelemetry(definition);

/**
 * ### `definePluginContext()`
 *
 * Standalone version of `definePlugin().context()` used to break down plugin definition.
 *
 * Its output must then be passed to `definePlugin().$context()`.
 *
 * @example
 * ```ts
 * const contextDef = definePluginContext(z.object({ name: z.string() }));
 * const plugin = definePlugin("my-plugin").$context(contextDef);
 *
 * // Equivalent to:
 * const plugin = definePlugin("my-plugin").context(z.object({ name: z.string() }));
 * ```
 * &nbsp;
 *
 * ---
 * @returns
 */
export const definePluginContext = <Schema extends z.ZodObject>(
  definition: ZodObjectWithTelemetry<Schema, "input">,
) => zodObjectWithTelemetry(definition);

/**
 * ### `definePluginEvents()`
 *
 * Standalone version of `definePlugin().events()` used to break down plugin definition.
 *
 * Its output must then be passed to `definePlugin().$events()`.
 *
 * @example
 * ```ts
 * const eventsDef = definePluginEvents([{ name: "my-event" }]);
 * const plugin = definePlugin("my-plugin").$events(eventsDef);
 *
 * // Equivalent to:
 * const plugin = definePlugin("my-plugin").events([{ name: "my-event" }]);
 * ```
 * &nbsp;
 *
 * ---
 * @returns The events definition.
 */
export const definePluginEvents = <
  const Names extends string[],
  EventsDef extends PluginEventsDefinition<Names>,
>(
  ...events: EventsDef
) => [...internalEventsDef, ...events] as unknown as typeof internalEventsDef | EventsDef;

/**
 * Builder class for the plugin definition.
 * Do not use directly, use `definePlugin()` instead.
 */
export class PluginBuilder<PluginDef extends PluginDefinition, Excluded extends string = never> {
  readonly def: PluginDef;

  constructor(definition: PluginDef) {
    this.def = definition;
  }

  /**
   * ### `.dependencies()`
   *
   * Specify other plugins as required by this plugin.
   *
   * Their context, events, and config can then be accessed from `dependencies.*` inside handlers.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .dependencies([anotherPlugin]);
   *    .addHandler({
   *        name: "handler-1",
   *        mode: "block",
   *        onEvent: ({ dependencies }) => {
   *            if (dependencies.anotherPlugin.config.delayMs > 10) { // <-- Here
   *              // ...
   *            }
   *        },
   *    });
   * ```
   * &nbsp;
   *
   * ---
   * @param plugins - The dependencies definitions.
   * @returns TypedPluginBuilder
   */
  dependencies<Plugins extends { def: PluginDefinition }[]>(plugins: Plugins) {
    const dependencies = plugins.map((p) => p.def);
    const builder = new PluginBuilder({ ...this.def, dependencies });
    return builder as TypedPluginBuilder<
      Override<
        PluginDef,
        "dependencies",
        {
          [K in keyof Plugins]: Without<Plugins[K]["def"], "dependencies">;
        }
      >,
      Excluded | "dependencies"
    >;
  }

  /**
   * ### `.config()`
   *
   * Add a configuration that users can provide to tweak plugin's behavior.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const myPlugin = definePlugin("my-plugin")
   *    .config({ schema: z.object({ enableVoice: z.boolean() }) });
   *
   * const myAgent = defineAgent("my-agent")
   *    .plugins([myPlugin])
   *    .myPlugin({ enableVoice: true }); // <-- Here
   * ```
   * &nbsp;
   *
   * ---
   *
   * The provided config can then be accessed from `plugin.config` inside handlers.
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .config({ schema: z.object({ enableVoice: z.boolean() }) });
   *    .addHandler({
   *      name: "handler-1",
   *      mode: "block",
   *      onEvent: ({ plugin }) => {
   *        if (plugin.config.enableVoice) { // <-- Here
   *          // ...
   *        }
   *      },
   *    });
   *
   * ```
   * &nbsp;
   *
   * ---
   * @param config - The config definition.
   * @returns TypedPluginBuilder
   */
  config<Schema extends z.ZodObject>(config: ZodObjectWithTelemetry<Schema, "input">) {
    return this.$config(zodObjectWithTelemetry(config));
  }

  /**
   * ### `.$config()`
   *
   * Define plugin config from the output of `definePluginConfig()`.
   *
   * @see TODO: Add docs link for `definePluginConfig()`
   *
   * ---
   * @param config - The config definition.
   * @returns TypedPluginBuilder
   */
  $config<ConfigDef extends PluginConfigDefinition>(config: ConfigDef) {
    const builder = new PluginBuilder({ ...this.def, config });
    return builder as TypedPluginBuilder<
      Override<PluginDef, "config", ConfigDef>,
      Excluded | "config" | "$config"
    >;
  }

  /**
   * ### `.context()`
   *
   * Add an in-memory object that handlers can read/write to share data without race conditions.
   *
   * Context is also exposed to dependent plugins and synced to the frontend via RPC.
   *
   * The context must be serializable, and thus cannot contain functions, instances, etc.
   * Use `state` property in handlers for non-serializable data instead.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .context(z.object({ name: z.string() }));
   * ```
   * &nbsp;
   *
   * ---
   * @param context - The context definition.
   */
  context<Schema extends z.ZodObject>(context: ZodObjectWithTelemetry<Schema, "input">) {
    return this.$context(zodObjectWithTelemetry(context));
  }

  /**
   * ### `.$context()`
   *
   * Define plugin context from the output of `definePluginContext()`.
   *
   * @see TODO: Add docs link for `definePluginContext()`
   *
   * ---
   * @param context - The context definition.
   * @returns TypedPluginBuilder
   */
  $context<ContextDef extends PluginContextDefinition>(context: ContextDef) {
    const builder = new PluginBuilder({ ...this.def, context });
    return builder as TypedPluginBuilder<
      Override<PluginDef, "context", ContextDef>,
      Excluded | "context" | "$context"
    >;
  }

  /**
   * ### `.events()`
   *
   * Add events that handlers can emit and observe to communicate with each other.
   *
   * Each plugin has a single event queue enforcing the order of execution, making
   * plugins more predictable and easier to write and maintain.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .events([{ name: "my-event" }])
   *    .addHandler({
   *      name: "handler-1",
   *      mode: "block",
   *      onEvent: ({ event, plugin }) => {
   *        // Emit 'my-event' when the plugin starts
   *        if (event.name === "plugin.start") {
   *          plugin.events.emit({ name: "my-event" });
   *        }
   *      },
   *    })
   *    .addHandler({
   *      name: "handler-2",
   *      mode: "block",
   *      onEvent: ({ event, plugin }) => {
   *        // Listen to 'my-event' from handler-1
   *        if (event.name === "my-event") {
   *          // Do something...
   *        }
   *      },
   *    });
   * ```
   * &nbsp;
   *
   * ---
   * @param events - The events definition.
   * @returns TypedPluginBuilder
   */
  events<const Names extends string[], EventsDef extends PluginEventsDefinition<Names>>(
    ...events: EventsDef
  ) {
    return this.$events([...internalEventsDef, ...events] as unknown as
      | typeof internalEventsDef
      | EventsDef);
  }

  /**
   * ### `.$events()`
   *
   * Define plugin events from the output of `definePluginEvents()`.
   *
   * @see TODO: Add docs link for `definePluginEvents()`
   *
   * ---
   * @param events - The events definition.
   * @returns TypedPluginBuilder
   */
  $events<EventsDef extends PluginEventsDefinition>(events: EventsDef) {
    const builder = new PluginBuilder({ ...this.def, events });
    return builder as TypedPluginBuilder<
      Override<PluginDef, "events", EventsDef>,
      Excluded | "events" | "$events"
    >;
  }

  /**
   * ### `.addHandler()`
   *
   * Add a handler function to react to events from this plugin or dependent plugins.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .addHandler({
   *      name: "handler-1",
   *      mode: "block",
   *      onEvent: ({ event, plugin }) => {
   *        // Do something...
   *      },
   *    });
   * ```
   * &nbsp;
   *
   * ---
   *
   * Handlers can return a value, which can be retrieved using the `events.waitForResult(eventId, handlerName)` method.
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .events([{ name: "my-event" }])
   *    .addHandler({
   *      name: "handler-1",
   *      mode: "block",
   *      onEvent: () => 123;
   *    })
   *    .addHandler({
   *      name: "handler-2",
   *      mode: "block",
   *      onEvent: ({ event, plugin }) => {
   *        if (event.name === "my-event") {
   *          const eventId = plugin.events.emit({ name: "my-event" });
   *          const result = await plugin.events.waitForResult(eventId, "handler-1");
   *          // result: string (type-safe!)
   *        }
   *      },
   *    })
   * ```
   * &nbsp;
   *
   * ---
   * @param handler - The handler definition.
   * @returns TypedPluginBuilder
   */
  addHandler<
    Name extends string,
    Handler extends PluginHandlerDefinition<PluginDef, Name, StateDef>,
    StateDef extends PluginHandlerStateDefinition<PluginDef>,
  >(handler: Handler & { state?: StateDef }) {
    const handlers = [...(this.def.handlers ?? []), handler];
    const builder = new PluginBuilder({ ...this.def, handlers });
    return builder as TypedPluginBuilder<
      // @ts-expect-error - type narrowing complaint, but works fine as is
      Override<
        PluginDef,
        "handlers",
        [
          ...PluginDef["handlers"],
          {
            name: Handler["name"];
            mode: Handler["mode"];
            state: never; // State is internal, no need to store its type
            onEvent: (params: unknown) => ReturnType<Handler["onEvent"]>; // Omit params to reduce type explosion
          },
        ]
      >,
      Excluded
    >;
  }

  /**
   * ### `.removeHandler()`
   *
   * Remove a previously added handler from the plugin.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const plugin = definePlugin("my-plugin")
   *    .addHandler({
   *      name: "handler-1",
   *      mode: "block",
   *      onEvent: ({ event, plugin }) => void 0,
   *    });
   *
   * const pluginWithoutHandler1 = plugin.removeHandler("handler-1");
   * ```
   * &nbsp;
   *
   * ---
   * @param name - The name of the handler to remove.
   * @returns TypedPluginBuilder
   */
  removeHandler<const Name extends PluginDef["handlers"][number]["name"]>(name: Name) {
    const handlers = this.def.handlers.filter((h) => h.name !== name);
    const builder = new PluginBuilder({ ...this.def, handlers });
    return builder as TypedPluginBuilder<
      Override<PluginDef, "handlers", FilterHandlersByName<PluginDef["handlers"], Name>>,
      Excluded
    >;
  }
}

/**
 * ### `definePlugin()`
 *
 * Define a new Life.js plugin.
 *
 * @see TODO: Add docs link
 */
export function definePlugin<const Name extends string>(name: Name) {
  return new PluginBuilder({
    name,
    dependencies: [],
    config: zodObjectWithTelemetry({ schema: z.object() }),
    context: zodObjectWithTelemetry({ schema: z.object() }),
    events: internalEventsDef,
    handlers: [],
  });
}
